import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTasks,
  faRefresh,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import { Client, QBOCompanyData } from '@types';
import { QBOCategory, QBODetailedTransaction } from '@shared/types/qbo_data';
import {
  canSendAmcEmail,
  getLastAmcEmail,
  getLastAmcEmailSentAt,
  AMC_EMAIL_MIN_INTERVAL_HOURS,
} from '@shared/utils/amcEmail';
import { Button } from 'react-daisyui';
import { useMemo, useState } from 'react';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import PageContainer from '@/components/PageContainer';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { get, post, put } from '@/utils/queries';
import { alertErrorMessage, alertMessageSuccess } from '@/utils/alerts';
import { LoadingPage } from '@/pages/LoadingPage';
import { EmptyState } from '@/components/EmptyState';
import ErrorAlert from '@/components/ErrorAlert/ErrorAlert';
import { Spinner } from '@/components/Spinner';
import GenericConfirmationModal from '@/components/Modal/GenericConfirmationModal';
import { get as getApi } from '@/utils/api';
import type { ApiRequestError } from '@/utils/api';
import { TableTransactionsClient } from './TableTransactionsClient';
import { TableTransactionsForControllers } from './TableTransactionsForControllers';
import { CompanySelectorDropdown } from './CompanySelector';
import { TransactionAutoComplete } from './TransactionAutoComplete';
import { MemoActionSelect } from './transactionsComponents';

export default function Transactions() {
  const [refreshInProgress, setRefreshInProgress] = useState<boolean>(false);
  const [selectedQBOCompanyId, setSelectedQBOCompanyId] = useState<number>(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkEditMode, setBulkEditMode] = useState<boolean>(false);
  const [batchKeepOriginal, setBatchKeepOriginal] = useState<boolean>(false);
  const [batchCategory, setBatchCategory] = useState<QBOCategory | undefined>();
  const { companyName, impersonatingAccountId, auth }: IUserContext =
    useUserContext();

  const transactionsListAPIUrl = '/transactions/fulldata';
  const {
    data: qboData,
    isValidating: isLoading,
    error,
    mutate: mutateQboData,
  } = impersonatingAccountId
    ? get<QBOCompanyData[]>(
        `${transactionsListAPIUrl}/${impersonatingAccountId}`
      )
    : get<QBOCompanyData[]>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${transactionsListAPIUrl}/${auth?.clients?.[0]?.id}`
      );

  const [isSavingReview, setIsSavingReview] = useState<boolean>(false);
  const [isPushing, setIsPushing] = useState<boolean>(false);
  const [isSendingReminder, setIsSendingReminder] = useState<boolean>(false);
  const [isReminderConfirmOpen, setIsReminderConfirmOpen] =
    useState<boolean>(false);

  // Persist a reviewer's decision. Mirrors deleteQBOCompany: build the expected
  // data first, call the API, and only patch the cache (no refetch) on success.
  const saveReview = async (reviewed: QBODetailedTransaction) => {
    const qboDataAfterReview = (qboData ?? []).map((company) => ({
      ...company,
      qbo_transactions: company.qbo_transactions.map((t) =>
        t.id === reviewed.id ? reviewed : t
      ),
    }));

    setIsSavingReview(true);
    try {
      await put(`transactions/${reviewed.id as number}/review`, {
        client_id: reviewed.client_id,
        keep_original_memo: !!reviewed.keep_original_memo,
        reviewed_qbo_categories_id: reviewed.reviewed_qbo_categories_id ?? null,
      });
      await mutateQboData(qboDataAfterReview, {
        revalidate: false,
        optimisticData: qboDataAfterReview,
      });
      alertMessageSuccess('Review saved.');
    } catch (err) {
      alertErrorMessage('Error while saving the review, try again later.');
    } finally {
      setIsSavingReview(false);
    }
  };

  const { data: clients } = get<Client[]>('account/list');

  const sortedCompanies = useMemo(
    () => {
      if (qboData && !isLoading) {
        return [...qboData].sort(
          (a, b) => b.qbo_transactions.length - a.qbo_transactions.length
        );
      }
      return [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qboData]
  );

  const selectedCompany = useMemo(
    () =>
      sortedCompanies.find((c) => c.id === selectedQBOCompanyId) ??
      sortedCompanies[0],
    [sortedCompanies, selectedQBOCompanyId]
  );

  const refreshQBOData = async () => {
    setRefreshInProgress(true);

    const clientId = impersonatingAccountId || auth?.clients?.[0]?.id;
    try {
      await getApi(`transactions/syncQBO/${clientId as number}`);
    } finally {
      setTimeout(() => setRefreshInProgress(false), 300);
    }
  };

  // Manual AMC reminder — client-wide, mirrors handleManualQboClientEmail. The
  // backend re-checks the 48h gate and whether there are pending transactions, so
  // a stale button can't force a double send.
  const sendReminderEmail = async () => {
    const clientId = impersonatingAccountId || auth?.clients?.[0]?.id;
    if (!clientId) return;

    setIsSendingReminder(true);
    try {
      const res = await post<{ message: string }>(
        `transactions/sendAmcEmail/${clientId}`,
        {}
      );
      alertMessageSuccess(res.message || 'Reminder email sent to the client.');
    } catch (err) {
      if ((err as ApiRequestError)?.status === 409) {
        alertErrorMessage((err as ApiRequestError).message);
      } else {
        alertErrorMessage(
          'Error while sending the reminder email, try again later.'
        );
      }
    } finally {
      // Refetch so last_manual_amc_email (and thus the button's enabled state) is
      // current whether or not the send went through.
      try {
        await mutateQboData();
      } catch {
        // Non-fatal: the button may stay briefly stale until the next load.
      }
      setIsSendingReminder(false);
      setIsReminderConfirmOpen(false);
    }
  };

  const handleCompanySelect = (companyId: number) => {
    setSelectedQBOCompanyId(companyId);
    // Selection and its batch controls are per company, so reset them when
    // switching companies (categories in particular are company-scoped).
    setSelectedIds(new Set());
    setBulkEditMode(false);
    setBatchCategory(undefined);
    setBatchKeepOriginal(false);
  };

  if (!qboData && isLoading) {
    return <LoadingPage hasNav />;
  }

  if (error) {
    return (
      <PageContainer name='client-list'>
        <DashboardWrapper
          companyName={companyName as string}
          companyData={clients}
        >
          <ErrorAlert
            isSuperAdmin={auth?.isSuperAdmin}
            error={error as unknown}
            context='Transactions'
          />
        </DashboardWrapper>
      </PageContainer>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (!sortedCompanies.length) {
    return (
      <PageContainer name='client-list'>
        <DashboardWrapper
          companyName={companyName as string}
          companyData={clients}
        >
          <EmptyState name='transactions' />
        </DashboardWrapper>
      </PageContainer>
    );
  }

  const adminTransactions = selectedCompany?.qbo_transactions ?? [];
  const respondedTransactions = adminTransactions.filter(
    (t) => !!t.modified_memo || !!t.modified_qbo_categories_id
  );
  const pendingTransactions = adminTransactions.filter(
    (t) => !t.modified_memo && !t.modified_qbo_categories_id
  );

  // The reminder is client-wide (all companies), enabled only when some company
  // still has an unanswered transaction and no reminder (automatic or manual)
  // went out in the last 48h. Mirrors handleManualQboClientEmail's gate.
  const hasUnansweredTransactions = sortedCompanies.some((company) =>
    company.qbo_transactions.some(
      (t) => !t.modified_memo && !t.modified_qbo_categories_id
    )
  );
  const reminderWithinThrottle = !canSendAmcEmail(sortedCompanies);
  const reminderDisabled =
    isSendingReminder || !hasUnansweredTransactions || reminderWithinThrottle;

  // When throttled, show when the reminder unlocks again (last email + 48h).
  // Rendered in the viewer's own timezone (with its abbreviation) so the time
  // matches their clock — the button re-enables at that instant, which in another
  // timezone would read as a different wall-clock time.
  const lastAmcEmailAt = getLastAmcEmailSentAt(sortedCompanies);
  const unlockLabel = lastAmcEmailAt
    ? new Date(
        lastAmcEmailAt.getTime() + AMC_EMAIL_MIN_INTERVAL_HOURS * 60 * 60 * 1000
      ).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

  let reminderTooltip = '';
  if (!hasUnansweredTransactions) {
    reminderTooltip = 'No pending client transactions to remind about.';
  } else if (reminderWithinThrottle) {
    reminderTooltip =
      'A reminder was sent in the last 48 hours. ' +
      `Available again ${unlockLabel}.`;
  }

  // Confirmation popup content: total unanswered transactions across every
  // company file, and a description of the most recent AMC email of any kind.
  const totalPendingTransactions = sortedCompanies.reduce(
    (sum, company) =>
      sum +
      company.qbo_transactions.filter(
        (t) => !t.modified_memo && !t.modified_qbo_categories_id
      ).length,
    0
  );
  const lastAmcEmail = getLastAmcEmail(sortedCompanies);
  let lastEmailLine = 'No AMC emails have been sent to this client yet.';
  if (lastAmcEmail) {
    const emailDate = new Date(lastAmcEmail.sentAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    lastEmailLine =
      lastAmcEmail.kind === 'automatic'
        ? `Last email: ${emailDate} (${lastAmcEmail.type}, automatic)`
        : `Last email: ${emailDate} (manual, sent by ${lastAmcEmail.sentBy})`;
  }

  // Pushing needs a reviewed category, so the push action is only enabled once
  // every selected transaction has one (bulk edit has no such requirement).
  const selectedTransactions = adminTransactions.filter((t) =>
    // `id` is a bigint column, so TypeORM hands it back as a string — coerce
    // it so it matches the numbers `selectedIds` actually holds.
    selectedIds.has(Number(t.id))
  );
  const allSelectedReviewed =
    selectedTransactions.length > 0 &&
    selectedTransactions.every((t) => !!t.reviewed_qbo_categories_id);

  const onToggleSelect = (id: number, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const onSelectMany = (ids: number[], checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });

  // Push the reviewed transactions to QBO. The backend removes the ones that
  // went through, so we drop those from the cache and the selection.
  const pushToQBO = async (ids: number[]) => {
    if (ids.length === 0 || !selectedCompany) return;
    const clientId = selectedCompany.client_id;

    setIsPushing(true);
    try {
      const res = await post<{
        message: string;
        results: { transactionId: number; updated: boolean }[];
      }>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `transactions/pushTransactions/${clientId as number}/${
          selectedCompany.id as number
        }`,
        { transactionIds: ids }
      );

      const pushedIds = (res.results ?? [])
        .filter((r) => r.updated)
        .map((r) => r.transactionId);

      // Optimistic version (kept for later): drops the pushed rows from the
      // cache without a refetch.
      // const qboDataAfterPush = (qboData ?? []).map((company) => ({
      //   ...company,
      //   qbo_transactions: company.qbo_transactions.filter(
      //     (t) => !pushedIds.includes(t.id as number)
      //   ),
      // }));
      // await mutateQboData(qboDataAfterPush, {
      //   revalidate: false,
      //   optimisticData: qboDataAfterPush,
      // });

      setSelectedIds((prev) => {
        const next = new Set(prev);
        pushedIds.forEach((id) => next.delete(id));
        return next;
      });
      alertMessageSuccess(res.message || 'Transactions pushed to QBO.');
    } catch (err) {
      alertErrorMessage(
        'Error while pushing transactions to QBO, try again later.'
      );
    } finally {
      // Refetch either way: the ones that went through leave the list, and the
      // ones that failed come back with an updated `tries` (error indicator).
      // Runs even when every push failed (the request rejects with a 502).
      try {
        await mutateQboData();
      } catch {
        alertErrorMessage(
          'Could not refresh the transactions list. Please reload the page.'
        );
      }
      setIsPushing(false);
    }
  };

  // Bulk-apply the review (one memo decision + one category) to every selected
  // transaction and push them to QBO in a single request. The backend resolves
  // the per-row memo fallback from `modified_memo`, so we only send the intent.
  // Mirrors pushToQBO: refetch in `finally` so the error indicators stay fresh.
  const applyBatchReview = async () => {
    if (!selectedCompany || !batchCategory?.id || selectedIds.size === 0)
      return;
    const clientId = selectedCompany.client_id;
    const ids = [...selectedIds];

    setIsPushing(true);
    try {
      const res = await post<{
        message: string;
        results: { transactionId: number; updated: boolean }[];
      }>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `transactions/reviewAndPush/${clientId as number}/${
          selectedCompany.id as number
        }`,
        {
          transactionIds: ids,
          keep_original_memo: batchKeepOriginal,
          reviewed_qbo_categories_id: batchCategory.id,
        }
      );

      const pushedIds = (res.results ?? [])
        .filter((r) => r.updated)
        .map((r) => r.transactionId);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pushedIds.forEach((id) => next.delete(id));
        return next;
      });
      alertMessageSuccess(res.message || 'Changes applied and pushed to QBO.');
    } catch (err) {
      alertErrorMessage('Error while applying the changes, try again later.');
    } finally {
      // The refetch runs regardless of the POST outcome. If it fails the list
      // on screen may be stale, so surface a reload hint instead of silently
      // swallowing it — but always reset below so the spinner never stays stuck.
      try {
        await mutateQboData();
      } catch {
        alertErrorMessage(
          'Could not refresh the transactions list. Please reload the page.'
        );
      }
      setBulkEditMode(false);
      setBatchCategory(undefined);
      setBatchKeepOriginal(false);
      setIsPushing(false);
    }
  };

  const client = clients?.find(
    (c) => c.id === (impersonatingAccountId || auth?.clients?.[0]?.id)
  );

  const selectionBarClass =
    'sticky top-2 z-40 mb-3 flex items-center justify-between gap-4 ' +
    'rounded-lg border border-accent/40 bg-base-100 px-4 py-2 shadow-md';

  return (
    <PageContainer name='transactions'>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
      <DashboardWrapper
        companyName={companyName as string}
        companyData={clients}
      >
        {auth?.isAdmin ? (
          <GenericConfirmationModal
            showModal={isReminderConfirmOpen}
            modalMessage={`Send a reminder email to ${
              client?.client_name ?? 'this client'
            }?`}
            isLoading={isSendingReminder}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onAcceptAction={() => sendReminderEmail()}
            onCloseAction={() => setIsReminderConfirmOpen(false)}
          >
            <div className='flex flex-col gap-2 text-sm'>
              <div>
                {totalPendingTransactions} transaction
                {totalPendingTransactions === 1 ? '' : 's'} awaiting a client
                response.
              </div>
              <div>{lastEmailLine}</div>
            </div>
          </GenericConfirmationModal>
        ) : null}
        <div className='p-4'>
          <div className='flex w-full justify-between items-center mb-2.5'>
            <h1 className='text-lg font-bold uppercase'>
              <FontAwesomeIcon
                icon={faTasks}
                size='lg'
                className='mr-4 text-white'
              />
              Help us Categorize these transactions
            </h1>
          </div>
          <CompanySelectorDropdown
            companies={sortedCompanies}
            selectedId={selectedCompany?.id}
            onSelect={handleCompanySelect}
          />
          <div className='flex w-full justify-between items-center my-2'>
            <div className='text-sm capitalize font-bold'>
              Transctions for: {selectedCompany?.name} - Pending Transactions:{' '}
              {selectedCompany?.pending_transactions}
            </div>
            {auth?.isAdmin ? (
              <div className='flex items-center'>
                <span
                  className={reminderTooltip ? 'tooltip tooltip-top' : ''}
                  data-tip={reminderTooltip || undefined}
                >
                  <Button
                    color='accent'
                    id='send-reminder'
                    size='sm'
                    className='btn btn-accent mr-2'
                    data-testid='send-reminder-button'
                    disabled={reminderDisabled}
                    onClick={() => setIsReminderConfirmOpen(true)}
                  >
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      size='lg'
                      className='mr-2 text-white'
                    />
                    Send reminder email
                  </Button>
                </span>
                <Button
                  color='accent'
                  id='refresh'
                  size='sm'
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={() => refreshQBOData()}
                  data-testid='refresh-button'
                >
                  <FontAwesomeIcon
                    icon={faRefresh}
                    size='lg'
                    className={`mr-2 text-white ${
                      refreshInProgress ? 'spinning' : ''
                    }`}
                  />
                  Refresh QBO Data
                </Button>
              </div>
            ) : null}
          </div>
          {auth?.isAdmin ? (
            <>
              {isSavingReview || isPushing ? (
                <div
                  className='fixed inset-0 z-[999] flex items-center justify-center bg-black/50'
                  data-testid='spinner-transaction'
                >
                  <Spinner className='h-1/4' />
                </div>
              ) : null}
              <hr />
              <br />
              {selectedIds.size > 0 ? (
                <div className={selectionBarClass}>
                  <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
                    <span className='text-sm font-semibold whitespace-nowrap'>
                      {selectedIds.size} selected
                    </span>
                    {bulkEditMode ? (
                      <>
                        <div className='flex items-center gap-2'>
                          <span className='text-xs font-semibold uppercase text-base-content/60'>
                            Approve Memo
                          </span>
                          <MemoActionSelect
                            testId='batch-memo-select'
                            keepOriginal={batchKeepOriginal}
                            onChange={setBatchKeepOriginal}
                          />
                        </div>
                        <div className='flex items-center gap-2'>
                          <span className='text-xs font-semibold uppercase text-base-content/60'>
                            Set Category
                          </span>
                          <div className='w-56'>
                            <TransactionAutoComplete
                              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                              key={`batch-cat-${selectedCompany?.id}`}
                              value={batchCategory?.name}
                              onOptionSelected={(c) => setBatchCategory(c)}
                              clientId={selectedCompany?.client_id as number}
                              qboCompanyId={selectedCompany?.id as number}
                            />
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>

                  <div className='flex shrink-0 items-center gap-2'>
                    {bulkEditMode ? (
                      <>
                        <Button
                          size='sm'
                          className='btn-secondary'
                          data-testid='batch-apply'
                          disabled={!batchCategory?.id}
                          // eslint-disable-next-line @typescript-eslint/no-misused-promises
                          onClick={applyBatchReview}
                        >
                          Apply and Push to QBO ({selectedIds.size})
                        </Button>
                        <Button
                          size='sm'
                          className='btn-ghost'
                          data-testid='bulk-edit-cancel'
                          onClick={() => setBulkEditMode(false)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size='sm'
                          className='btn-outline'
                          data-testid='bulk-edit'
                          onClick={() => setBulkEditMode(true)}
                        >
                          Bulk edit and push
                        </Button>
                        <span
                          className={
                            allSelectedReviewed ? '' : 'tooltip tooltip-top'
                          }
                          data-tip='Every selected transaction needs a reviewed category to push.'
                        >
                          <Button
                            color='accent'
                            size='sm'
                            data-testid='push-to-qbo'
                            disabled={!allSelectedReviewed}
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            onClick={async () => {
                              await pushToQBO([...selectedIds]);
                            }}
                          >
                            Push {selectedIds.size} to QBO
                          </Button>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              ) : null}
              <div className='text-lg my-2 capitalize font-bold'>
                Client responded ({respondedTransactions.length})
              </div>
              <TableTransactionsForControllers
                transactions={respondedTransactions}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                onSelectMany={onSelectMany}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSaveReview={async (transaction) => {
                  await saveReview(transaction);
                }}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onPushToQBO={async (ids) => {
                  await pushToQBO(ids);
                }}
              />
              <div className='text-lg mt-6 mb-2 capitalize font-bold'>
                Pending response ({pendingTransactions.length})
              </div>
              <TableTransactionsForControllers
                transactions={pendingTransactions}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                onSelectMany={onSelectMany}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSaveReview={async (transaction) => {
                  await saveReview(transaction);
                }}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onPushToQBO={async (ids) => {
                  await pushToQBO(ids);
                }}
              />
            </>
          ) : (
            <TableTransactionsClient
              transactions={selectedCompany?.qbo_transactions ?? []}
            />
          )}
        </div>
      </DashboardWrapper>
    </PageContainer>
  );
}
